import { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import Sidebar from "../components/Sidebar";
import NewNeedModal from "../components/NewNeedModal";
import NewRecruitmentModal from "../components/NewRecruitmentModal";
import { SlotsTab, RecruitmentCandidatesTab } from "../components/NeedDetailTabs";
import { i18nLocale, weekdayLabel } from "../lib/locale";
import { useNavigate } from "react-router-dom";
import {
  Plus, Calendar, MapPin, Trash2, Clock, Copy, Eye, EyeOff, Pencil,
  AlertTriangle, ChevronLeft, Euro, FileText, Users, CheckCircle2, Send, Lock,
} from "lucide-react";

const STATUS_STYLE = {
  draft:  { bg: "#F0F2EF", fg: "#6B726A" },
  open:   { bg: "#E8F0EA", fg: "#1A4D2E" },
  filled: { bg: "#FBEFDA", fg: "#C97A20" },
  closed: { bg: "#FFF4F3", fg: "#E3655B" },
};

const SEARCH_STATUS_STYLE = {
  searching: { background: "#E8F0EA", color: "#1A4D2E" },
  filled:    { background: "#FBEFDA", color: "#C97A20" },
  confirmed: { background: "#E8F0EA", color: "#1A4D2E" },
  completed: { background: "#E8E5FF", color: "#3F2EB2" },
  expired:   { background: "#F0F2EF", color: "#6B726A" },
  closed:    { background: "#FFF4F3", color: "#E3655B" },
  draft:     { background: "#F0F2EF", color: "#6B726A" },
};

const badgeFor = (item, t) => {
  if (item._kind === "recruitment") {
    const ct = item.contract_type || "";
    if (ct.startsWith("CDI")) return { label: "CDI", color: "#2FB673" };
    if (ct.startsWith("CDD")) return { label: "CDD", color: "#F0783C" };
    return { label: t("needs_page.badge_alternance"), color: "#7048A8" };
  }
  if (item.mode === "permanent") return { label: t("needs_page.type_recurrent"), color: "#7048A8" };
  return { label: t("needs_page.badge_replacement"), color: "#3B5BDB" };
};

const fmtDate = (iso) => {
  try {
    const d = new Date(`${iso}T00:00:00`);
    return d.toLocaleDateString(i18nLocale(), { weekday: "short", day: "numeric", month: "short", year: "numeric" });
  } catch { return iso; }
};

const contractInfo = (item, t) => {
  if (item._kind === "recruitment") {
    return `${item.contract_type}${item.work_time ? ` · ${item.work_time}` : ""}`;
  }
  if (item.mode === "permanent") {
    const n = (item.weekdays || []).length;
    return n ? t("needs_page.days_per_week", { count: n }) : t("needs_page.type_recurrent");
  }
  const count = (item.dates || []).length;
  if (count) {
    const sorted = [...item.dates].sort();
    return t("needs_page.days_from", { count, date: fmtDate(sorted[0]) });
  }
  if (item.start_date) return `${fmtDate(item.start_date)}${item.end_date ? ` → ${fmtDate(item.end_date)}` : ""}`;
  return "—";
};

const shortRef = (id) => "#" + String(id).slice(0, 4).toUpperCase();

export default function Needs() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [createMode, setCreateMode] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [mobileDetailOpen, setMobileDetailOpen] = useState(false);
  const [detailTab, setDetailTab] = useState("description");

  const load = async () => {
    const [{ data: needsData }, { data: recData }] = await Promise.all([
      api.get("/needs"),
      api.get("/recruitments"),
    ]);
    const merged = [
      ...needsData.map((n) => ({ ...n, _kind: "need" })),
      ...recData.map((r) => ({ ...r, _kind: "recruitment" })),
    ].sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || "")));
    setItems(merged);
    if (merged.length && !merged.find((n) => n.id === selectedId)) {
      setSelectedId(merged[0].id);
    }
  };
  useEffect(() => { load(); }, []);

  const selected = useMemo(() => items.find((n) => n.id === selectedId) || null, [items, selectedId]);

  const del = async (item) => {
    await api.delete(item._kind === "recruitment" ? `/recruitments/${item.id}` : `/needs/${item.id}`);
    setConfirmDelete(null);
    if (selectedId === item.id) setSelectedId(null);
    load();
  };

  const togglePublish = async (n) => {
    const newStatus = n.status === "open" ? "draft" : "open";
    if (n._kind === "recruitment") {
      await api.patch(`/recruitments/${n.id}`, { status: newStatus });
    } else {
      await api.patch(`/needs/${n.id}/status`, { status: newStatus });
    }
    load();
  };

  const duplicate = async (id) => {
    const { data } = await api.post(`/needs/${id}/duplicate`);
    await load();
    setSelectedId(data.id);
  };

  const openSelected = (id) => {
    setSelectedId(id);
    setDetailTab("description");
    setMobileDetailOpen(true);
  };

  return (
    <div className="flex bg-[#F7F8F6] min-h-screen">
      <Sidebar />
      <main className="flex-1 min-w-0">
        <div className="max-w-[1440px] mx-auto p-6 lg:p-10">
          <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
            <div>
              <h1 className="text-3xl sm:text-4xl font-medium tracking-tight" style={{ fontFamily: "Outfit" }}>{t("needs.title")}</h1>
              <p className="text-sm text-[#6B726A] mt-1">{t("needs.subtitle")}</p>
            </div>
            <button onClick={() => setCreateMode("temporary")} data-testid="needs-new-button" className="rp-btn-primary flex items-center gap-2">
              <Plus className="w-4 h-4" /> {t("dashboard.new_need")}
            </button>
          </div>

          {items.length === 0 ? (
            <div className="rp-card p-12 text-center">
              <p className="text-[#6B726A] text-sm">{t("needs.empty")}</p>
              <button onClick={() => setCreateMode("temporary")} className="rp-btn-primary mt-4">{t("needs.create_first")}</button>
            </div>
          ) : (
            <div className={`grid gap-6 ${detailTab === "slots" ? "grid-cols-1" : "lg:grid-cols-[380px_1fr]"}`}>
              {/* LEFT: list */}
              <aside className={`${detailTab === "slots" ? "hidden" : mobileDetailOpen ? "hidden lg:block" : "block"}`} data-testid="needs-list">
                <div className="bg-white rounded-2xl border border-[#E2E5E0] overflow-hidden">
                  {items.map((n) => {
                    const st = STATUS_STYLE[n.status] || STATUS_STYLE.open;
                    const bd = badgeFor(n, t);
                    const isRecruit = n._kind === "recruitment";
                    const active = n.id === selectedId;
                    return (
                      <button key={n.id}
                        data-testid={`need-row-${n.id}`}
                        onClick={() => openSelected(n.id)}
                        className={`w-full text-left p-4 border-b border-[#E2E5E0] last:border-b-0 transition-colors ${
                          active ? "bg-[#E8F0EA] border-l-4 border-l-[#1A4D2E]" : "hover:bg-[#F7F8F6] border-l-4 border-l-transparent"
                        }`}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 text-xs mb-1">
                              <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: bd.color }} />
                              <span className="font-semibold" style={{ color: bd.color }}
                                data-testid={`need-type-${n.id}`}>{bd.label}</span>
                              <span className="font-mono text-[#6B726A]">{shortRef(n.id)}</span>
                              {n.urgent && (
                                <span className="inline-flex items-center gap-0.5 text-[#E3655B]">
                                  <AlertTriangle className="w-3 h-3" /> {t("needs_page.urgent")}
                                </span>
                              )}
                            </div>
                            <div className="font-medium text-sm truncate" data-testid={`need-row-title-${n.id}`}>
                              {isRecruit ? n.title : (n.role_type || t("needs_page.role_fallback"))}
                            </div>
                            <div className="text-xs text-[#6B726A] mt-1 truncate" data-testid={`need-row-contract-${n.id}`}>
                              {contractInfo(n, t)}
                            </div>
                            <div className="text-xs text-[#9AA097] mt-0.5 truncate">{n.address || "—"}</div>
                          </div>
                          <div className="flex flex-col items-end gap-1 shrink-0">
                            <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-full whitespace-nowrap"
                              style={{ background: st.bg, color: st.fg }}
                              data-testid={`need-status-${n.id}`}>{t(`status.${n.status}`, n.status)}</span>
                          </div>
                        </div>
                        <div className="flex items-center mt-3 pt-3 border-t border-[#E2E5E0]/70">
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <div className="w-7 h-7 rounded-full bg-[#E8F0EA] grid place-items-center shrink-0">
                                <Send className="w-3.5 h-3.5 text-[#1A9E55]" />
                              </div>
                              <div className="min-w-0">
                                <div className="text-[10px] text-[#6B726A] leading-tight">{t("needs_page.kpi_notified")}</div>
                                <div className="text-sm font-semibold text-[#1A4D2E]" data-testid={`need-notified-count-${n.id}`}>{n.notified_count ?? 0}</div>
                              </div>
                            </div>
                            <div className="w-px h-8 bg-[#E2E5E0] mx-2 shrink-0" />
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <div className="w-7 h-7 rounded-full bg-[#E0ECFF] grid place-items-center shrink-0">
                                <span className="w-2.5 h-2.5 rounded-full bg-[#3B5BDB]" />
                              </div>
                              <div className="min-w-0">
                                <div className="text-[10px] text-[#6B726A] leading-tight">{t("needs_page.kpi_status")}</div>
                                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap inline-block"
                                  style={SEARCH_STATUS_STYLE[n.search_status] || SEARCH_STATUS_STYLE.searching}
                                  data-testid={`need-search-status-${n.id}`}>
                                  {n.search_status === "searching" ? t("status.to_fill") : n.search_status === "confirmed" ? t("status.confirmed_short") : t(`status.${n.search_status}`, n.search_status)}
                                </span>
                              </div>
                            </div>
                            <div className="w-px h-8 bg-[#E2E5E0] mx-2 shrink-0" />
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <div className="w-7 h-7 rounded-full bg-[#F1EAFB] grid place-items-center shrink-0">
                                <Users className="w-3.5 h-3.5 text-[#7048A8]" />
                              </div>
                              <div className="min-w-0">
                                <div className="text-[10px] text-[#6B726A] leading-tight">{t("needs_page.candidatures")}</div>
                                <div className="text-sm font-semibold text-[#7048A8]" data-testid={`need-apps-${n.id}`}>{n.applications_count ?? 0}</div>
                              </div>
                            </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </aside>

              {/* RIGHT: detail */}
              <section className={`lg:block ${mobileDetailOpen ? "block" : "hidden"}`} data-testid="need-detail-pane">
                {selected ? (
                  selected._kind === "recruitment" ? (
                    <RecruitmentDetail
                      n={selected}
                      onEdit={() => setEditTarget(selected)}
                      onDelete={() => setConfirmDelete(selected)}
                      onTogglePublish={() => togglePublish(selected)}
                      onBack={() => setMobileDetailOpen(false)}
                    />
                  ) : (
                    <NeedDetail
                      n={selected}
                      onEdit={() => setEditTarget(selected)}
                      onDelete={() => setConfirmDelete(selected)}
                      onDuplicate={() => duplicate(selected.id)}
                      onTogglePublish={() => togglePublish(selected)}
                      onBack={() => setMobileDetailOpen(false)}
                      onChanged={load}
                      onTabChange={setDetailTab}
                    />
                  )
                ) : (
                  <div className="rp-card p-12 text-center text-sm text-[#6B726A]">
                    {t("needs_page.select_need")}
                  </div>
                )}
              </section>
            </div>
          )}
        </div>
      </main>

      <NewNeedModal
        open={createMode === "temporary"}
        onClose={() => setCreateMode(null)}
        onSwitchMode={(m) => setCreateMode(m)}
        onCreated={(created) => { load(); if (created?.id) setSelectedId(created.id); }}
      />
      <NewRecruitmentModal
        open={createMode === "permanent"}
        onClose={() => setCreateMode(null)}
        onSwitchMode={(m) => setCreateMode(m)}
        onCreated={(created) => { load(); if (created?.id) setSelectedId(created.id); }}
      />

      <NewNeedModal
        open={!!editTarget && editTarget._kind !== "recruitment"}
        onClose={() => setEditTarget(null)}
        existing={editTarget && editTarget._kind !== "recruitment" ? editTarget : null}
        onCreated={() => { setEditTarget(null); load(); }}
      />
      <NewRecruitmentModal
        open={!!editTarget && editTarget._kind === "recruitment"}
        onClose={() => setEditTarget(null)}
        existing={editTarget && editTarget._kind === "recruitment" ? editTarget : null}
        onCreated={() => { setEditTarget(null); load(); }}
      />

      {confirmDelete && (
        <ConfirmModal
          title={confirmDelete._kind === "recruitment" ? t("needs_page.delete_recruit_title") : t("needs_page.delete_need_title")}
          desc={t("needs_page.delete_desc", { ref: shortRef(confirmDelete.id), label: confirmDelete._kind === "recruitment" ? confirmDelete.title : confirmDelete.role_type })}
          confirmLabel={t("needs_page.delete")}
          cancelLabel={t("common.cancel")}
          danger
          onClose={() => setConfirmDelete(null)}
          onConfirm={() => del(confirmDelete)}
        />
      )}
    </div>
  );
}

function NeedDetail({ n, onEdit, onDelete, onDuplicate, onTogglePublish, onBack, onChanged, onTabChange }) {
  const { t } = useTranslation();
  const st = STATUS_STYLE[n.status] || STATUS_STYLE.open;
  const published = n.status === "open";
  const [tab, setTab] = useState("description");
  const [bookings, setBookings] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [stats, setStats] = useState(null);

  const loadBookings = async () => {
    try {
      const { data } = await api.get("/bookings");
      setBookings((Array.isArray(data) ? data : []).filter((b) => b.need_id === n.id));
    } catch { setBookings([]); }
  };
  const loadProposals = () => {
    api.get(`/needs/${n.id}/proposals`)
      .then(({ data }) => setProposals(Array.isArray(data) ? data : []))
      .catch(() => setProposals([]));
  };
  useEffect(() => {
    setTab("description");
    loadBookings();
    loadProposals();
    api.get(`/needs/${n.id}/stats`)
      .then(({ data }) => setStats(data))
      .catch(() => setStats(null));
  }, [n.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const changeTab = (k) => { setTab(k); onTabChange?.(k); };
  const refresh = () => { loadBookings(); loadProposals(); onChanged?.(); };

  const TABS = [
    { key: "description", label: t("needs_page.tab_description") },
    { key: "slots",       label: n.mode === "permanent" ? t("needs_page.tab_permanent_candidates", "Candidats permanents") : t("needs_page.tab_slots") },
  ];

  return (
    <div className="bg-white rounded-2xl border border-[#E2E5E0]">
      <div className="p-6 pb-0 border-b border-[#E2E5E0]">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <button onClick={onBack} className="lg:hidden mb-2 inline-flex items-center gap-1 text-xs text-[#6B726A] hover:text-[#1f2421]" data-testid="detail-back-mobile">
              <ChevronLeft className="w-3.5 h-3.5" /> {t("needs_page.back_to_list")}
            </button>
            <div className="flex items-center gap-2 text-xs text-[#6B726A]">
              <span className="font-mono">{shortRef(n.id)}</span>
              <span>·</span>
              <span className="capitalize">{n.mode === "permanent" ? t("needs_page.type_recurrent") : t("needs_page.type_temporary")}</span>
              {n.urgent && (
                <span className="inline-flex items-center gap-0.5 text-[#E3655B] ml-1">
                  <AlertTriangle className="w-3 h-3" /> {t("needs_page.urgent")}
                </span>
              )}
            </div>
            <h2 className="text-2xl font-medium mt-1" style={{ fontFamily: "Outfit" }} data-testid="detail-title">
              {n.role_type || t("needs_page.role_fallback")}
            </h2>
          </div>
          <span className="text-[10px] uppercase tracking-wider font-semibold px-2.5 py-1 rounded-full"
            style={{ background: st.bg, color: st.fg }}
            data-testid="detail-status">{t(`status.${n.status}`, n.status)}</span>
        </div>
        <div className="flex gap-1 mt-5 -mb-px" data-testid="need-detail-tabs">
          {TABS.map((tb) => (
            <button key={tb.key} onClick={() => changeTab(tb.key)} data-testid={`need-tab-${tb.key}`}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                tab === tb.key
                  ? "border-[#1A4D2E] text-[#1A4D2E]"
                  : "border-transparent text-[#6B726A] hover:text-[#1f2421]"
              }`}>
              {tb.label}
              {tb.key === "candidates" && typeof n.applications_count === "number" && n.applications_count > 0 && (
                <span className="ml-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-[#E8F0EA] text-[#1A4D2E]">{n.applications_count}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="p-6">
        {tab === "description" && (
          <div className="space-y-7" data-testid="need-tab-content-description">
            <Section title={t("needs_page.section_general")} icon={FileText}>
              <KV k={t("needs_page.role_wanted")} v={n.role_type || "—"} />
              <KV k={t("needs_page.mode")} v={n.mode === "permanent" ? t("needs_page.mode_recurrent") : t("needs_page.mode_temporary")} />
              {typeof n.applications_count === "number" && (
                <KV k={t("needs_page.candidatures")} v={t("needs_page.applications", { count: n.applications_count })} />
              )}
              {n.skills?.length > 0 && (
                <KV k={t("needs_page.skills")} v={
                  <div className="flex flex-wrap gap-1.5">
                    {n.skills.map((s) => (
                      <span key={s} className="text-[11px] px-2 py-0.5 rounded-full border border-[#E2E5E0] text-[#6B726A]">{s}</span>
                    ))}
                  </div>
                } />
              )}
              {n.notes && <KV k={t("needs_page.comments")} v={n.notes} />}
            </Section>

            <Section title={t("needs_page.section_dates")} icon={Calendar}>
              <DateSlots n={n} />
            </Section>

            <Section title={t("needs_page.section_extra")} icon={MapPin}>
              <KV k={t("needs_page.location")} v={n.address || "—"} />
              <KV k={t("needs_page.rate")} v={n.hourly_rate ? t("needs_page.rate_value", { rate: n.hourly_rate }) : "—"} />
              {n.urgent && <KV k={t("needs_page.special_conditions")} v={t("needs_page.urgent_notified")} />}
            </Section>
          </div>
        )}

        {tab === "slots" && (
          <SlotsTab need={n} bookings={bookings} proposals={proposals} stats={stats} onChanged={refresh} />
        )}
      </div>

      {proposals.some((p) => p.status === "confirmed") ? (
        <div className="p-6 border-t border-[#BBE3C8] bg-[#EFF9F2] flex items-center gap-2.5 text-sm text-[#14532D]" data-testid="detail-locked-bar">
          <Lock className="w-4 h-4 shrink-0 text-[#166534]" />
          {t("needs_page.locked_actions")}
        </div>
      ) : (
        <div className="p-6 border-t border-[#E2E5E0] flex flex-wrap gap-2">
          <ActionBtn icon={Pencil} label={t("needs_page.edit")} onClick={onEdit} testid="detail-edit" />
          <ActionBtn icon={Copy} label={t("needs_page.duplicate")} onClick={onDuplicate} testid="detail-duplicate" />
          <ActionBtn
            icon={published ? EyeOff : Eye}
            label={published ? t("needs_page.unpublish") : t("needs_page.publish")}
            onClick={onTogglePublish}
            testid="detail-toggle-publish"
          />
          <ActionBtn icon={Trash2} label={t("needs_page.delete")} danger onClick={onDelete} testid="detail-delete" />
        </div>
      )}
    </div>
  );
}

function RecruitmentDetail({ n, onEdit, onDelete, onTogglePublish, onBack }) {
  const { t } = useTranslation();
  const [tab, setTab] = useState("description");
  const st = STATUS_STYLE[n.status] || STATUS_STYLE.open;
  const published = n.status === "open";
  return (
    <div className="bg-white rounded-2xl border border-[#E2E5E0]">
      <div className="p-6 pb-0 border-b border-[#E2E5E0]">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <button onClick={onBack} className="lg:hidden mb-2 inline-flex items-center gap-1 text-xs text-[#6B726A]" data-testid="detail-back-mobile">
              <ChevronLeft className="w-3.5 h-3.5" /> {t("needs_page.back_to_list")}
            </button>
            <div className="flex items-center gap-2 text-xs text-[#6B726A]">
              <span className="font-mono">{shortRef(n.id)}</span>
              <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full"
                style={{ background: "#E8EDF7", color: "#3B5BDB" }}>{t("needs_page.type_permanent")}</span>
              <span>{n.contract_type} · {n.work_time}</span>
            </div>
            <h2 className="text-2xl font-medium mt-1" style={{ fontFamily: "Outfit" }} data-testid="detail-title">{n.title}</h2>
          </div>
          <span className="text-[10px] uppercase tracking-wider font-semibold px-2.5 py-1 rounded-full"
            style={{ background: st.bg, color: st.fg }} data-testid="detail-status">{t(`status.${n.status}`, n.status)}</span>
        </div>
        <div className="flex gap-1 mt-5 -mb-px" data-testid="recruit-detail-tabs">
          {[
            { key: "description", label: t("needs_page.tab_description") },
            { key: "candidates", label: t("needs_page.tab_permanent_candidates", "Candidats permanents") },
          ].map((tb) => (
            <button key={tb.key} onClick={() => setTab(tb.key)} data-testid={`recruit-tab-${tb.key}`}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                tab === tb.key
                  ? "border-[#1A4D2E] text-[#1A4D2E]"
                  : "border-transparent text-[#6B726A] hover:text-[#1f2421]"
              }`}>
              {tb.label}
            </button>
          ))}
        </div>
      </div>

      {tab === "candidates" ? (
        <div className="p-6">
          <RecruitmentCandidatesTab recruitment={n} />
        </div>
      ) : (
      <div className="p-6 space-y-7">
        <Section title={t("needs_page.section_general")} icon={FileText}>
          <KV k={t("needs_page.position")} v={n.role_type} />
          <KV k={t("needs_page.contract")} v={n.contract_type} />
          <KV k={t("needs_page.work_time")} v={`${n.work_time}${n.weekly_hours ? ` ${t("needs_page.weekly_hours", { h: n.weekly_hours })}` : ""}`} />
          <KV k={t("needs_page.experience")} v={n.experience || "—"} />
          {(n.skills || []).length > 0 && <KV k={t("needs_page.skills")} v={<Chips items={n.skills} />} />}
          {(n.languages || []).length > 0 && <KV k={t("needs_page.languages")} v={<Chips items={n.languages} />} />}
          {(n.softwares || []).length > 0 && <KV k={t("needs_page.softwares")} v={<Chips items={n.softwares} />} />}
        </Section>

        <Section title={t("needs_page.section_salary")} icon={Euro}>
          <KV k={t("needs_page.salary")} v={n.salary_min || n.salary_max ? t("needs_page.salary_range", { min: n.salary_min || "?", max: n.salary_max || "?" }) : t("needs_page.not_specified")} />
          {n.bonus_objectives && <KV k={t("needs_page.bonus")} v={t("common.yes")} />}
          {n.thirteenth_month && <KV k={t("needs_page.thirteenth")} v={t("common.yes")} />}
          {n.profit_sharing && <KV k={t("needs_page.profit_sharing")} v={t("common.yes")} />}
        </Section>

        <Section title={t("needs_page.section_benefits")} icon={CheckCircle2}>
          <div className="grid sm:grid-cols-2 gap-2">
            {n.housing_provided && <Bullet text={t("needs_page.housing")} />}
            {n.housing_contribution && <Bullet text={t("needs_page.housing_help")} />}
            {n.company_car && <Bullet text={t("needs_page.car")} />}
            {n.meal_vouchers && <Bullet text={t("needs_page.meal")} />}
            {n.mutuelle && <Bullet text={t("needs_page.mutuelle")} />}
            {n.transport && <Bullet text={t("needs_page.transport")} />}
            {!(n.housing_provided || n.housing_contribution || n.company_car || n.meal_vouchers || n.mutuelle || n.transport) && (
              <div className="text-sm text-[#6B726A]">{t("needs_page.no_benefits")}</div>
            )}
          </div>
        </Section>

        <Section title={t("needs_page.section_org")} icon={Clock}>
          {(n.weekdays || []).length > 0 && (
            <KV k={t("needs_page.worked_days")} v={(n.weekdays || []).map((k) => weekdayLabel(k)).join(", ")} />
          )}
          <div className="grid sm:grid-cols-2 gap-2">
            {n.flexible_hours && <Bullet text={t("needs_page.flexible")} />}
            {n.saturday_work && <Bullet text={t("needs_page.saturday_on")} />}
            {!n.saturday_work && <Bullet text={t("needs_page.saturday_off")} />}
            {n.sunday_work && <Bullet text={t("needs_page.sunday_on")} />}
            {n.night_guards && <Bullet text={t("needs_page.night_guards")} />}
            {n.weekend_guards && <Bullet text={t("needs_page.weekend_guards")} />}
            {n.continuous_hours && <Bullet text={t("needs_page.continuous")} />}
          </div>
        </Section>

        <Section title={t("needs_page.section_place")} icon={MapPin}>
          <KV k={t("needs_page.address")} v={n.address} />
          {n.notes && <KV k={t("needs_page.comments")} v={n.notes} />}
        </Section>
      </div>
      )}

      <div className="p-6 border-t border-[#E2E5E0] flex flex-wrap gap-2">
        <ActionBtn icon={Pencil} label={t("needs_page.edit")} onClick={onEdit} testid="detail-edit" />
        <ActionBtn
          icon={published ? EyeOff : Eye}
          label={published ? t("needs_page.unpublish") : t("needs_page.publish")}
          onClick={onTogglePublish}
          testid="detail-toggle-publish"
        />
        <ActionBtn icon={Trash2} label={t("needs_page.delete")} danger onClick={onDelete} testid="detail-delete" />
      </div>
    </div>
  );
}

function DateSlots({ n }) {
  const isPermanent = n.mode === "permanent";
  const keys = isPermanent ? (n.weekdays || []) : [...(n.dates || [])].sort();

  if (keys.length === 0) {
    return (
      <div className="text-sm text-[#1f2421]">
        <div className="flex items-center gap-2"><Calendar className="w-3.5 h-3.5 text-[#6B726A]" /> {fmtDate(n.start_date)} → {fmtDate(n.end_date)}</div>
        {(n.start_time || n.end_time) && (
          <div className="flex items-center gap-2 mt-1"><Clock className="w-3.5 h-3.5 text-[#6B726A]" /> {n.start_time || "—"} – {n.end_time || "—"}</div>
        )}
      </div>
    );
  }

  const dayLabel = (k) => isPermanent ? weekdayLabel(k) : fmtDate(k);
  const slotsFor = (k) => (n.per_day && n.per_day[k]?.slots) || n.global_slots || [];

  return (
    <div className="space-y-2" data-testid="detail-date-slots">
      {keys.map((k) => {
        const slots = slotsFor(k);
        return (
          <div key={k} className="flex items-start gap-3 p-3 rounded-xl bg-[#F7F8F6]">
            <CheckCircle2 className="w-4 h-4 text-[#1A4D2E] mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium capitalize">{dayLabel(k)}</div>
              <div className="text-xs text-[#6B726A] mt-0.5 flex flex-wrap gap-x-2">
                {slots.length > 0
                  ? slots.map((s, i) => <span key={i}>{s.start} - {s.end}</span>)
                  : <span>—</span>}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div>
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-semibold text-[#6B726A] mb-3">
        <Icon className="w-3.5 h-3.5" /> {title}
      </div>
      <div className="space-y-2.5">{children}</div>
    </div>
  );
}

function KV({ k, v }) {
  return (
    <div className="flex items-start justify-between gap-4 py-1.5 border-b border-dashed border-[#E2E5E0] last:border-b-0">
      <span className="text-xs text-[#6B726A] shrink-0">{k}</span>
      <span className="text-sm font-medium text-[#1f2421] text-right">{v}</span>
    </div>
  );
}

function Chips({ items }) {
  return <div className="flex flex-wrap gap-1.5 justify-end">{items.map((s) => <span key={s} className="text-[11px] px-2 py-0.5 rounded-full border border-[#E2E5E0] text-[#6B726A]">{s}</span>)}</div>;
}

function Bullet({ text }) {
  return <div className="flex items-center gap-2 text-sm"><CheckCircle2 className="w-3.5 h-3.5 text-[#1A4D2E]" />{text}</div>;
}

function ActionBtn({ icon: Icon, label, onClick, primary, danger, testid }) {
  const base = "inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors";
  if (primary) {
    return <button onClick={onClick} data-testid={testid} className={`${base} bg-[#1A4D2E] text-white hover:bg-[#143C24]`}><Icon className="w-4 h-4" /> {label}</button>;
  }
  if (danger) {
    return <button onClick={onClick} data-testid={testid} className={`${base} border border-[#E3655B]/30 text-[#E3655B] hover:bg-[#FFF4F3]`}><Icon className="w-4 h-4" /> {label}</button>;
  }
  return <button onClick={onClick} data-testid={testid} className={`${base} border border-[#E2E5E0] text-[#1f2421] hover:bg-[#F7F8F6]`}><Icon className="w-4 h-4" /> {label}</button>;
}

function ConfirmModal({ title, desc, confirmLabel, cancelLabel, onClose, onConfirm, danger }) {
  return (
    <div className="fixed inset-0 z-[80] bg-black/50 backdrop-blur-sm grid place-items-center p-4" onClick={onClose} data-testid="confirm-modal-overlay">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-3xl max-w-md w-full p-7 shadow-2xl" data-testid="confirm-modal">
        <div className={`w-12 h-12 rounded-2xl grid place-items-center mb-4 ${danger ? "bg-[#FFF4F3]" : "bg-[#E8F0EA]"}`}>
          <AlertTriangle className={`w-5 h-5 ${danger ? "text-[#E3655B]" : "text-[#1A4D2E]"}`} />
        </div>
        <h3 className="text-lg font-medium mb-2" style={{ fontFamily: "Outfit" }}>{title}</h3>
        <p className="text-sm text-[#6B726A] mb-6">{desc}</p>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-full text-sm text-[#6B726A] hover:bg-[#F0F2EF]" data-testid="confirm-cancel">{cancelLabel}</button>
          <button onClick={onConfirm} data-testid="confirm-confirm"
            className={`px-4 py-2 rounded-full text-sm text-white font-medium ${danger ? "bg-[#E3655B] hover:bg-[#C44A41]" : "bg-[#1A4D2E] hover:bg-[#143C24]"}`}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
